import React, { useState } from "react";
import { Check, ChevronDown, ChevronUp, Gauge, Timer, Zap, ListChecks } from "lucide-react";
import { MODES } from "@/config/adaptiveForm";

const MODE_ICONS = { "2min": Zap, "5min": Timer, full: ListChecks };

export function ModePicker({ value, onChange, compact = false }) {
  return (
    <div className={`grid ${compact ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-3"} gap-3`} data-testid="mode-picker">
      {MODES.map((m) => {
        const Icon = MODE_ICONS[m.key];
        const active = value === m.key;
        return (
          <button
            key={m.key}
            type="button"
            onClick={() => onChange(m.key)}
            className={`text-left rounded-xl border-2 p-4 transition ${
              active ? "border-amber-500 bg-amber-50/60 ring-2 ring-amber-200" : "border-gray-200 hover:border-amber-300 bg-white"
            }`}
            data-testid={`mode-${m.key}`}
          >
            <div className="flex items-center gap-2">
              <Icon className={`h-4 w-4 ${active ? "text-amber-600" : "text-gray-500"}`} />
              <span className="font-display font-semibold text-sm text-gray-900">{m.label}</span>
              {active && <Check className="h-4 w-4 text-amber-600 ml-auto" />}
            </div>
            <div className="text-xs text-gray-500 mt-1.5">{m.desc}</div>
          </button>
        );
      })}
    </div>
  );
}

/** Collapsible optional section. Shows a completion tick + count when closed. */
export function CollapsedSection({ title, project, fieldKeys = [], children, testId }) {
  const [open, setOpen] = useState(false);
  const filled = fieldKeys.filter((k) => {
    const v = project?.[k];
    if (Array.isArray(v)) return v.length > 0;
    return !!(v && String(v).trim());
  }).length;
  return (
    <div className="rounded-xl border border-dashed border-gray-300 overflow-hidden" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50/70 hover:bg-amber-50/50 transition text-left"
        data-testid={`${testId}-toggle`}
      >
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-800">
          {filled > 0 && (
            <span className="h-5 w-5 rounded-full bg-emerald-500 text-white inline-flex items-center justify-center">
              <Check className="h-3 w-3" />
            </span>
          )}
          {title}
        </span>
        <span className="inline-flex items-center gap-2 text-xs text-gray-500">
          {filled > 0 && !open && <span>{filled} completed</span>}
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {open && <div className="p-4 space-y-5 bg-white">{children}</div>}
    </div>
  );
}

export function ScorePill({ score, status, suggestions = [] }) {
  const [open, setOpen] = useState(false);
  const colour =
    score >= 85 ? "bg-emerald-100 text-emerald-800" : score >= 65 ? "bg-amber-100 text-amber-800" : score >= 40 ? "bg-sky-100 text-sky-700" : "bg-gray-100 text-gray-600";
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${colour}`}
        data-testid="capture-score"
        title="Project Capture Score"
      >
        <Gauge className="h-3.5 w-3.5" /> {score}% · {status}
      </button>
      {open && suggestions.length > 0 && (
        <div className="absolute right-0 top-8 z-40 w-64 rounded-xl border border-gray-200 bg-white shadow-lg p-3" data-testid="score-suggestions">
          <div className="text-[11px] uppercase tracking-widest text-gray-400 font-bold mb-2">Improve this record</div>
          <ul className="space-y-1.5">
            {suggestions.map((s) => (
              <li key={s} className="text-xs text-gray-700 flex gap-2">
                <span className="text-amber-500 font-bold">→</span> {s}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
